'use client';

import React, { useCallback, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Toast, useToast } from '@/shared/ui/feedback/Toast';
import { copyToClipboard } from '@/shared/lib/clipboard';
import { formatPhoneNumber, toTelHref } from '@/shared/lib/formatPhone';
import {
  LocationOnIcon,
  AccessTimeIcon,
  LocalShippingIcon,
  PlayArrowIcon,
} from '@/shared/ui/display/EmojiIcon';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import type { MyVisitCounselDetailData } from '@/entities/visit-counsel/model/useMyVisitCounselDetail';
import {
  formatStructuredAddress,
  formatStructuredAddressTwoLine,
  VisitCounselState,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import { getIntakeDepartureAddress } from '@/entities/visit-counsel/model/intake-helpers';
import { VisitCounselStatusChip, counselStateToVariant } from '@/entities/visit-counsel';
import { formatScheduleTimeRange } from '@/shared/lib/timeslot/timeslot.types';
import { CounselLocationMap } from './CounselLocationMap';
import { SectionLabel } from '@/shared/ui/data/SectionLabel';
import { InfoRow } from '@/shared/ui/data/InfoRow';
import {
  FloatingCard,
  PageHeader,
  Button as DsButton,
  IconButton as DsIconButton,
} from '@miso/design-system';

dayjs.locale('ko');

function formatDate(date: string) {
  return dayjs(date).format('YYYY년 M월 D일 (ddd)');
}

/** 컴팩트 섹션 래퍼 (카드 대신 단순 구분선) */
const SECTION_SX = {
  '&:not(:first-of-type)': {
    mt: 1.5,
    pt: 1.5,
    borderTop: '1px solid',
    borderColor: 'divider',
  },
};

export interface CounselDetailBodyProps {
  data: MyVisitCounselDetailData;
  isDesktop: boolean;
  isMobile: boolean;
  /** 상담 시작 클릭 시 출발지 입력 페이지로 이동 */
  onNavigateToStart?: (visitCounselId: string) => void;
  /** 이어하기 클릭 시 상담 진행 페이지로 이동 */
  onNavigateToProgress?: (visitCounselId: string) => void;
  /** 모바일: 뒤로가기 (컴팩트 헤더용) */
  onBack?: () => void;
}

export function CounselDetailBody({
  data,
  isDesktop,
  isMobile,
  onNavigateToStart,
  onNavigateToProgress,
  onBack,
}: CounselDetailBodyProps) {
  const { toast, showToast, hideToast } = useToast();
  const [isExpanded, setIsExpanded] = useState(true);
  const { counsel, order, events, customer } = data;

  const customerPhone = customer?.phone ?? order?.ordererPhone ?? '';
  const moveDate = order?.moving?.moveDate ?? '';
  const isStarted = counsel.state === VisitCounselState.STARTED;
  const depAddr = getIntakeDepartureAddress(counsel);
  const departureAddress =
    isStarted && depAddr != null
      ? formatStructuredAddress(depAddr)
      : (order?.moving?.departure?.location ?? counsel.location);
  const departureSupplyArea = order?.moving?.departure?.supplyArea ?? counsel.supplyArea;
  const arrivalAddress = order?.moving?.arrival?.location ?? '';
  const arrivalSupplyArea = order?.moving?.arrival?.supplyArea ?? 0;

  const handleCopy = useCallback(
    async (text: string, label: string) => {
      const ok = await copyToClipboard(text);
      showToast(ok ? `${label} 복사됨` : '복사 실패', ok ? 'success' : 'error');
    },
    [showToast]
  );

  const handleStartOrProgress = isStarted ? onNavigateToProgress : onNavigateToStart;
  const location = order?.assignedDepartureLocation ?? null;
  const roadAddress = (() => {
    if (location?.roadAddress) return location.roadAddress;
    if (depAddr) {
      const { primaryLine } = formatStructuredAddressTwoLine(depAddr);
      return primaryLine;
    }
    return counsel.location ?? '';
  })();
  const appointedEvent = events.events.find((e) => e.event?.eventType === 'Appointed');
  const appointedAt = appointedEvent?.event.occurredAt;

  /** 주소 2줄: 도로명주소 / 상세주소 */
  const addressLines = (() => {
    if (depAddr) {
      const { primaryLine, secondaryLine } = formatStructuredAddressTwoLine(depAddr);
      return { line1: primaryLine, line2: secondaryLine || undefined };
    }
    const roadAddr = location?.roadAddress;
    if (roadAddr && counsel.location?.trim()) {
      const rest = counsel.location.replace(roadAddr, '').trim();
      return { line1: roadAddr, line2: rest || undefined };
    }
    return { line1: counsel.location, line2: undefined };
  })();

  if (isMobile && onBack) {
    const headerTitle = `${formatScheduleTimeRange(counsel.scheduleTime, 'am-pm')} 상담`;

    return (
      <>
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 200,
            width: '100%',
            height: '100%',
          }}
        >
          {/* 지도: position absolute로 명시적 크기 확보 (Mapbox 렌더링 필수) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CounselLocationMap
              location={location}
              address={counsel.location}
              visitCounselId={counsel.id}
              scheduleTime={counsel.scheduleTime}
              fill
              hideNavigateButton
            />
          </Box>

          {/* 상단 헤더 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 20,
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            <PageHeader title={headerTitle} onBack={onBack} backLabel="뒤로가기" />
          </div>

          {/* 하단 FloatingCard */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              padding: '20px 12px 24px',
              maxHeight: 'calc(100% - 76px)',
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'stretch',
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', width: '100%' }}>
              <FloatingCard expanded={isExpanded} onExpandedChange={setIsExpanded}>
                <FloatingCard.CollapseButton onPress={onBack} aria-label="뒤로가기" />
                <FloatingCard.Body>
                  <FloatingCard.Header>
                    <FloatingCard.Eyebrow>
                      {formatScheduleTimeRange(counsel.scheduleTime, 'am-pm')}
                    </FloatingCard.Eyebrow>
                    <FloatingCard.TitleRow>
                      <FloatingCard.Title>{addressLines.line1}</FloatingCard.Title>
                      <FloatingCard.ExpandChevron />
                    </FloatingCard.TitleRow>
                    {addressLines.line2 && (
                      <FloatingCard.Subtitle>{addressLines.line2}</FloatingCard.Subtitle>
                    )}
                    <FloatingCard.TagGroup>
                      <FloatingCard.Tag>{counsel.supplyArea}평</FloatingCard.Tag>
                    </FloatingCard.TagGroup>
                  </FloatingCard.Header>

                  <FloatingCard.Details>
                    <FloatingCard.Section>
                      <FloatingCard.SectionLabel>이사 정보</FloatingCard.SectionLabel>
                      <FloatingCard.SectionContent>
                        <FloatingCard.Row>{moveDate ? formatDate(moveDate) : '-'}</FloatingCard.Row>
                        <FloatingCard.Row>
                          {arrivalAddress
                            ? `${arrivalAddress}${arrivalSupplyArea ? ` (${arrivalSupplyArea}평)` : ''}`
                            : '-'}
                        </FloatingCard.Row>
                      </FloatingCard.SectionContent>
                    </FloatingCard.Section>
                    {appointedEvent && (
                      <FloatingCard.Section>
                        <FloatingCard.SectionLabel>이력</FloatingCard.SectionLabel>
                        <FloatingCard.SectionContent>
                          <FloatingCard.Row>
                            상담 배정 {appointedAt ? dayjs(appointedAt).format('M/D HH:mm') : ''}
                          </FloatingCard.Row>
                        </FloatingCard.SectionContent>
                      </FloatingCard.Section>
                    )}
                  </FloatingCard.Details>

                  <FloatingCard.ActionBar>
                    <FloatingCard.SubButtonGroup>
                      {customerPhone && toTelHref(customerPhone) && (
                        <DsIconButton
                          variant="default"
                          size="m"
                          icon={<LocalPhoneOutlinedIcon />}
                          aria-label={`통화 ${formatPhoneNumber(customerPhone)}`}
                          onPress={() => {
                            const href = toTelHref(customerPhone);
                            if (href) window.location.href = href;
                          }}
                        />
                      )}
                      <DsIconButton
                        variant="default"
                        size="m"
                        icon={<ContentCopyOutlinedIcon />}
                        aria-label="주소 복사"
                        onPress={() => handleCopy(roadAddress, '주소')}
                      />
                    </FloatingCard.SubButtonGroup>
                    <FloatingCard.MainCTA>
                      <DsButton
                        variant="primary"
                        size="s"
                        onPress={() => handleStartOrProgress?.(counsel.id)}
                        disabled={!handleStartOrProgress}
                      >
                        {isStarted ? '이어하기' : '상담 시작'}
                      </DsButton>
                    </FloatingCard.MainCTA>
                  </FloatingCard.ActionBar>
                </FloatingCard.Body>
              </FloatingCard>
            </div>
          </div>
        </Box>
        <Toast
          open={!!toast}
          message={toast?.message ?? ''}
          severity={toast?.severity ?? 'neutral'}
          autoHideDuration={2000}
          onClose={hideToast}
          position="bottom"
          horizontal="center"
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: isDesktop ? 'grid' : 'flex',
          gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
          gap: isDesktop ? 3 : 0,
          flexDirection: 'column',
          p: { xs: 1.5, sm: 2 },
          maxWidth: isDesktop ? 1100 : undefined,
          mx: isDesktop ? 'auto' : undefined,
        }}
      >
        {/* 좌측: 정보 리스트 (당근마켓 스타일 컴팩트) */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {!isDesktop && (
            <Box sx={SECTION_SX}>
              <SectionLabel icon={<LocationOnIcon fontSize="small" />} label="방문 위치" />
              <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden' }}>
                <CounselLocationMap
                  location={location}
                  address={counsel.location}
                  visitCounselId={counsel.id}
                  scheduleTime={counsel.scheduleTime}
                />
              </Box>
            </Box>
          )}

          <Box sx={SECTION_SX}>
            <SectionLabel icon={<LocalPhoneOutlinedIcon fontSize="small" />} label="고객 연락처" />
            <InfoRow
              label="전화번호"
              value={formatPhoneNumber(customerPhone)}
              href={toTelHref(customerPhone) ?? undefined}
            />
          </Box>

          <Box sx={SECTION_SX}>
            <SectionLabel icon={<AccessTimeIcon fontSize="small" />} label="방문 상담 정보" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  minWidth: 68,
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                }}
              >
                상담 상태
              </Typography>
              <VisitCounselStatusChip variant={counselStateToVariant(counsel)} />
            </Box>
            <InfoRow
              label="방문일시"
              value={`${formatDate(counsel.scheduleDate)} ${formatScheduleTimeRange(counsel.scheduleTime, 'am-pm')}`}
            />
            <InfoRow
              label="방문 주소"
              value={counsel.location}
              onCopy={() => handleCopy(counsel.location, '주소')}
            />
            <InfoRow label="공급면적" value={`${counsel.supplyArea}평`} />
          </Box>

          <Box sx={SECTION_SX}>
            <SectionLabel icon={<LocalShippingIcon fontSize="small" />} label="이사 정보" />
            <InfoRow label="이사 예정일" value={moveDate ? formatDate(moveDate) : '-'} />
            <InfoRow
              label="출발지"
              value={`${departureAddress} (${departureSupplyArea ?? 0}평)`}
              onCopy={() => handleCopy(departureAddress, '출발지 주소')}
            />
            <InfoRow
              label="도착지"
              value={arrivalAddress ? `${arrivalAddress} (${arrivalSupplyArea}평)` : '-'}
              onCopy={arrivalAddress ? () => handleCopy(arrivalAddress, '도착지 주소') : undefined}
            />
          </Box>

          <Box sx={SECTION_SX}>
            <SectionLabel icon={<AccessTimeIcon fontSize="small" />} label="이력" />
            <Box sx={{ py: 0.5 }}>
              {appointedEvent ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                    상담 배정
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {appointedAt ? dayjs(appointedAt).format('M/D HH:mm') : '-'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  이벤트 없음
                </Typography>
              )}
            </Box>
          </Box>

          {isDesktop && (
            <Box sx={{ ...SECTION_SX, pt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                액션
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                  disabled={!handleStartOrProgress}
                  onClick={() => handleStartOrProgress?.(counsel.id)}
                  sx={{ fontWeight: 600 }}
                >
                  {isStarted ? '이어하기' : '상담 시작'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => handleCopy(counsel.location, '주소')}
                >
                  주소 복사
                </Button>
                {customerPhone && toTelHref(customerPhone) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocalPhoneOutlinedIcon sx={{ fontSize: 16 }} />}
                    component="a"
                    href={toTelHref(customerPhone)!}
                  >
                    통화 {formatPhoneNumber(customerPhone)}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {isDesktop && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ position: 'sticky', top: 16, borderRadius: 1, overflow: 'hidden' }}>
              <SectionLabel icon={<LocationOnIcon fontSize="small" />} label="방문 위치" />
              <Box sx={{ mt: 0.5 }}>
                <CounselLocationMap
                  location={location}
                  address={counsel.location}
                  visitCounselId={counsel.id}
                  scheduleTime={counsel.scheduleTime}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Toast
        open={!!toast}
        message={toast?.message ?? ''}
        severity={toast?.severity ?? 'neutral'}
        autoHideDuration={2000}
        onClose={hideToast}
        position="bottom"
        horizontal="center"
      />
    </>
  );
}
